import React, { useState, useRef, useEffect } from "react";
import { addDays, endOfDay, startOfDay, subYears } from "date-fns";

import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import InfoIcon from "../../../images/error-info-icon.svg";

import "./datepicker.scss";

import PropTypes from "prop-types";
import { DateRangePicker } from 'react-date-range';
import { useAppSelector } from "../../../hooks/storeHooks";
import { handleInputChange } from "../../well-controlroom-charts/common";
import { formateDate } from "./DefaultRanges";
import { showToaster } from "../../dashboard/components/asset-location/AssetList";

interface DateRangeSelectorProps {
     isOpen?: boolean;
     ranges?: any;
     onChange?: any;
     onSubmit?: any;
     setShowCalendar?: any;
     rest?: any;
     setSelectedDateRange: any;
     staticRanges: any;
     minDate: any;
     maxDate: any;
     selectedDateRange: {
          startDate: Date;
          endDate: Date;
          key: string;
     };
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ setShowCalendar, setSelectedDateRange, staticRanges, minDate, maxDate, selectedDateRange }) => {
     const selectedAssetDetails = useAppSelector((state) => state.assetDetail.assetDetail?.assetDetails);
     const [calendar, setCalendar] = useState<{
          startDate: Date | undefined;
          endDate: Date | undefined;
          key: string;
     }>({
          startDate: selectedDateRange?.startDate || startOfDay(addDays(new Date(), -6)),
          endDate: selectedDateRange?.endDate || endOfDay(new Date()),
          key: selectedDateRange?.key || "selection"
     })
     const [rawInput, setRawInput] = useState<{
          startDate: string;
          endDate: string;
     }>({
          startDate: formateDate(selectedDateRange?.startDate || startOfDay(new Date())),
          endDate: formateDate(selectedDateRange?.endDate || endOfDay(new Date())),
     });
     const [error, setError] = useState<string | null>(null);
     const dateRef = useRef<HTMLDivElement>(null);
     const [updatedRanges, setUpdatedRanges] = useState<any>(staticRanges);
     const [customMinDate, setCustomMinDate] = useState<any>(minDate);

     const handleSelect = (ranges: any) => {
          setCalendar(ranges.selection);
          setRawInput({
               startDate: formateDate(ranges.selection.startDate),
               endDate: formateDate(ranges.selection.endDate),
          });
          setError(null);
     };

     const handleApply = () => {
          if (!error) {
               setSelectedDateRange({
                    startDate: calendar.startDate,
                    endDate: calendar.endDate,
                    key: calendar.key
               });
               setShowCalendar(false);
          } else {
               showToaster(error, "error");
          }
     };

     const handleOnClose = () => {
          setShowCalendar(false);
     }

     const handleClickOutside = (event: MouseEvent) => {
          if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
               setShowCalendar(false);
          }
     };

     useEffect(() => {
          document.addEventListener('mousedown', handleClickOutside);
          return () => {
               document.removeEventListener('mousedown', handleClickOutside);
          };
     }, []);

     useEffect(() => {
          const updateRanges = async () => {
               const assetCreationDateFromLocalStorage = sessionStorage.getItem('assetCreationDate') || "";

               const assetCreationDate = selectedAssetDetails?.creationDateTime
                    ? new Date(selectedAssetDetails.creationDateTime)
                    : (assetCreationDateFromLocalStorage && new Date(assetCreationDateFromLocalStorage))
                    || subYears(new Date(), 2);
               const date1 = new Date(customMinDate);
               const date2 = new Date(assetCreationDate);
               const differenceInTime = date1?.getTime() - date2?.getTime();
               const differenceInDays = differenceInTime / (1000 * 3600 * 24);
               if (assetCreationDate) {
                    setUpdatedRanges((prev: any[]) => prev.map((range: { label: string; }) => {
                         if (range.label === 'All Time') {
                              if (differenceInDays > 0) {
                                   setCustomMinDate(date2);
                              }
                              return {
                                   ...range,
                                   range: () => ({
                                        startDate: assetCreationDate,
                                        endDate: endOfDay(new Date())
                                   })
                              };
                         }
                         return range;
                    }));
               }
          };

          updateRanges();
     }, [selectedAssetDetails]);

     return (
          <React.Fragment>
               <div className="date-picker__container" ref={dateRef} >
                    <DateRangePicker
                         onChange={handleSelect}
                         moveRangeOnFirstSelection={false}
                         months={2}
                         ranges={[calendar]}
                         direction="horizontal"
                         staticRanges={updatedRanges}
                         minDate={customMinDate}
                         maxDate={maxDate}
                    />
                    <div className="text-right position-relative rdr-buttons-position d-flex">
                         <div className='date-picker__footer-container'>
                              <div className='date-picker__footer-left-container'></div>
                              <div className='date-picker__footer-rigth-container'>
                                   <div className="date-picker__input-container">
                                        <input
                                             className={`date-picker__input pl-10 ${error ? "!border-red-400 focus-visible:!outline-none" : ""}`}
                                             value={rawInput.startDate}
                                             onChange={(e) => handleInputChange("startDate", e.target.value, rawInput, setRawInput, setCalendar, setError, calendar, customMinDate)}
                                        />
                                        <span className="mx-2">-</span>
                                        <div className="relative mt-0" >
                                             <input
                                                  className={`date-picker__input pl-10 ${error ? "!border-red-400 focus-visible:!outline-none !w-[130px]" : ""}`}
                                                  value={rawInput.endDate}
                                                  onChange={(e) => handleInputChange("endDate", e.target.value, rawInput, setRawInput, setCalendar, setError, calendar, customMinDate)}
                                             />
                                             {error && <img
                                                  src={InfoIcon}
                                                  alt="Info Icon"
                                                  className="absolute left-[100px] top-[13px] w-[20px]"
                                             />}
                                             {error && <p className="!text-red-400 mt-3">{error}</p>}
                                        </div>
                                   </div>
                                   <div className="date-picker__button-container" >
                                        <button
                                             className="date-picker__btn-secondary"
                                             onClick={() => handleOnClose()}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             disabled={error ? true : false}
                                             className={`date-picker__btn-primary ${error ? "opacity-50 cursor-not-allowed" : ""}`}
                                             onClick={handleApply}
                                        >
                                             Apply
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          </React.Fragment>
     );
};

// DateRangeSelector.defaultProps = {
//      ranges: defaultStaticRanges
// };

DateRangeSelector.propTypes = {
     /**
      * On Submit
      */
     onSubmit: PropTypes.func
};

export default DateRangeSelector;
